import { useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import { Check, Loader2, HelpCircle, AlertCircle, RefreshCw } from "lucide-react";
import { LowConfidenceIcon } from "@/components/LowConfidenceIcon";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { extractionApi } from "@/services/extractionApi";
import { AI_SCORE_DEFAULT, AI_SCORE_LOW_CONFIDENCE_THRESHOLD } from "@/config/aiScoring";
import { parseCellCitations, removeCitationBrackets } from "@/utils/parseCitations";
import type { Document } from "@/types/models";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";

interface TemplateColumn {
  name: string;
  priming: string;
}

interface FieldDataWithAI {
  value: string;
  status: string;
  aiScore?: number;
  aiSuggestions?: string[];
  aiAlternativeAnswer?: string;
}

interface DataValidationSheetProps {
  open: boolean;
  onClose: () => void;
  studyName: string;
  fields: Record<string, string>;
  fieldsData?: Record<string, FieldDataWithAI>;
  documentId: string;
  sourcefile: string;
  templateColumns: TemplateColumn[];
  documents?: Document[];
  reviewApiName?: string;
  model?: string;
  onStatusChange?: (status: "Pending" | "Reviewed") => void;
  onSave?: (
    updatedFields: Record<string, string>,
    approvedFieldName?: string,
    aiScores?: Record<string, number>,
    aiSuggestions?: Record<string, string[]>,
    aiAlternativeAnswers?: Record<string, string>
  ) => Promise<void>;
}

export interface DataValidationSheetRef {
  checkUnsavedChanges: (proceed: () => void) => void;
}

const getButtonClassName = (
  isSaving: boolean,
  isApproved: boolean
): string => {
  if (isSaving) {
    return "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed";
  }
  if (isApproved) {
    return "bg-green-600 border-green-600 text-white hover:bg-green-700";
  }
  return "bg-white border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50";
};


export const DataValidationSheet = forwardRef<DataValidationSheetRef, DataValidationSheetProps>(({
  open,
  onClose,
  studyName,
  fields,
  fieldsData,
  documentId,
  sourcefile,
  templateColumns,
  documents = [],
  reviewApiName,
  model,
  onStatusChange,
  onSave,
}, ref) => {
  const { openDocument, viewingDoc, closeDocument } = useDocumentViewer();
  const [fieldApprovals, setFieldApprovals] = useState<Record<string, boolean>>({});
  const [editedFields, setEditedFields] = useState<Record<string, string>>({});
  const [aiScores, setAiScores] = useState<Record<string, number>>({});
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, string[]>>({});
  const [aiAlternativeAnswers, setAiAlternativeAnswers] = useState<Record<string, string>>({});
  const [appliedAlternativeAnswers, setAppliedAlternativeAnswers] = useState<Set<string>>(new Set());
  const [isLoadingScores, setIsLoadingScores] = useState(false);
  const [refreshingScoreFields, setRefreshingScoreFields] = useState<Set<string>>(new Set());
  const [savingFields, setSavingFields] = useState<Set<string>>(new Set());
  const [savingStatus, setSavingStatus] = useState<"Pending" | "Reviewed" | null>(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingProceed, setPendingProceed] = useState<(() => void) | null>(null);

  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const scoreAbortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const bulkScoreAbortControllerRef = useRef<AbortController | null>(null);
  const activeDocumentIdRef = useRef<string>(documentId);
  const savedFieldsRef = useRef<Record<string, string>>(fields);

  // Refs to track latest state for unmount auto-save
  const editedFieldsRef = useRef<Record<string, string>>(fields);
  const aiScoresRef = useRef<Record<string, number>>({});
  const aiSuggestionsRef = useRef<Record<string, string[]>>({});
  const aiAlternativeAnswersRef = useRef<Record<string, string>>({});
  const discardedRef = useRef(false);

  const prevStatusRef = useRef<"Pending" | "Reviewed" | null>(null);

  const status = useMemo(() => {
    const fieldKeys = Object.keys(editedFields);
    return fieldKeys.length > 0 && fieldKeys.every(key => fieldApprovals[key])
      ? "Reviewed"
      : "Pending";
  }, [fieldApprovals, editedFields]);

  const hasUnsavedChanges = useMemo(
    () =>
      appliedAlternativeAnswers.size > 0 ||
      Object.keys(editedFields).some(key => editedFields[key] !== savedFieldsRef.current[key]),
    [editedFields, appliedAlternativeAnswers]
  );

  // Keep refs in sync for unmount auto-save
  useEffect(() => { editedFieldsRef.current = editedFields; }, [editedFields]);
  useEffect(() => { aiScoresRef.current = aiScores; }, [aiScores]);
  useEffect(() => { aiSuggestionsRef.current = aiSuggestions; }, [aiSuggestions]);
  useEffect(() => { aiAlternativeAnswersRef.current = aiAlternativeAnswers; }, [aiAlternativeAnswers]);

  // Auto-save on unmount if there are unsaved changes (guards against parent-driven unmount bypassing checkUnsavedChanges)
  useEffect(() => {
    return () => {
      const current = editedFieldsRef.current;
      const saved = savedFieldsRef.current;
      const hasDirty = Object.keys(current).some(key => current[key] !== saved[key]);
      if (hasDirty && !discardedRef.current) {
        onSaveRef.current?.(
          current,
          undefined,
          aiScoresRef.current,
          aiSuggestionsRef.current,
          aiAlternativeAnswersRef.current
        );
      }
    };
  }, []);

  useEffect(() => {
    if (!open || !documentId || !sourcefile) return;

    setEditedFields(fields);
    savedFieldsRef.current = fields;
    // Reset scores and alternative answers so stale values from a prior extraction never bleed through
    setAiScores({});
    setAiSuggestions({});
    setAiAlternativeAnswers({});

    const initialApprovals: Record<string, boolean> = {};
    const existingScores: Record<string, number> = {};
    const existingSuggestions: Record<string, string[]> = {};
    const existingAlternativeAnswers: Record<string, string> = {};
    let hasAllScores = true;

    if (fieldsData) {
      Object.entries(fieldsData).forEach(([fieldName, fieldData]) => {
        initialApprovals[fieldName] = fieldData.status === "Reviewed" || fieldData.status === "Approved";

        if (fieldData.aiScore !== undefined) {
          existingScores[fieldName] = fieldData.aiScore;
        } else {
          hasAllScores = false;
        }

        if (fieldData.aiSuggestions) {
          existingSuggestions[fieldName] = fieldData.aiSuggestions;
        }

        if (fieldData.aiAlternativeAnswer) {
          existingAlternativeAnswers[fieldName] = fieldData.aiAlternativeAnswer;
        }
      });
    } else {
      hasAllScores = false;
    }

    setFieldApprovals(initialApprovals);
    setAppliedAlternativeAnswers(new Set());

    // If all fields are already reviewed, mark prevStatusRef so the auto-update
    // effect doesn't re-trigger an unnecessary API call and show loaders.
    const allAlreadyReviewed = Object.keys(initialApprovals).length > 0 &&
      Object.values(initialApprovals).every(Boolean);
    if (allAlreadyReviewed) {
      prevStatusRef.current = "Reviewed";
    } else {
      prevStatusRef.current = null;
    }

    // Also re-fetch if any low-confidence field is missing a persisted alternative answer —
    // covers rows scored before the backend started returning alternative_answer.
    const missingAlternativeAnswer = hasAllScores && Object.entries(existingScores).some(
      ([fieldName, score]) =>
        score < AI_SCORE_LOW_CONFIDENCE_THRESHOLD && !existingAlternativeAnswers[fieldName]
    );

    if ((hasAllScores && !missingAlternativeAnswer) || allAlreadyReviewed) {
      setAiScores(existingScores);
      setAiSuggestions(existingSuggestions);
      setAiAlternativeAnswers(existingAlternativeAnswers);
      setIsLoadingScores(false);
      return;
    }

    setIsLoadingScores(true);

    // Abort any previous in-flight bulk score fetch and capture the current document ID
    bulkScoreAbortControllerRef.current?.abort();
    const bulkAbortController = new AbortController();
    bulkScoreAbortControllerRef.current = bulkAbortController;
    activeDocumentIdRef.current = documentId;
    const capturedDocumentId = documentId;

    const columns = Object.entries(fields).map(([columnName, columnValue]) => {
      const templateColumn = templateColumns.find(col => col.name === columnName);
      const question = templateColumn?.priming || `What is the ${columnName.toLowerCase()}?`;

      return {
        column_name: columnName,
        column_value: columnValue,
        question,
      };
    });

    const reviewInput = {
      sourcefile,
      document_id: documentId,
      columns,
      priming: "",
      model: model ?? "opus-46",
    };
    (reviewApiName
      ? extractionApi.reviewDataAtPath(reviewApiName, reviewInput)
      : extractionApi.reviewData(reviewInput, bulkAbortController.signal)
    )
      .then(async (results) => {
        // Guard: discard results if the user switched to a different document while the fetch was in-flight
        if (bulkAbortController.signal.aborted || activeDocumentIdRef.current !== capturedDocumentId) {
          return;
        }

        if (results?.length > 0) {
          const scores: Record<string, number> = {};
          const suggestions: Record<string, string[]> = {};
          const alternativeAnswers: Record<string, string> = {};

          results[0].columns.forEach((col: { column_name: string; score: number; suggestions?: string[]; alternative_answer?: string }) => {
            scores[col.column_name] = col.score;
            suggestions[col.column_name] = col.suggestions || [];
            if (col.alternative_answer) {
              alternativeAnswers[col.column_name] = col.alternative_answer;
            }
          });

          setAiScores(scores);
          setAiSuggestions(suggestions);
          setAiAlternativeAnswers(alternativeAnswers);

          await onSaveRef.current?.(fields, undefined, scores, suggestions, alternativeAnswers);
        }
      })
      .catch((err) => {
        if ((err as { name?: string }).name === "AbortError") return;
        if (activeDocumentIdRef.current !== capturedDocumentId) return;
        const fallbackScores: Record<string, number> = {};
        Object.keys(fields).forEach(fieldName => {
          fallbackScores[fieldName] = AI_SCORE_DEFAULT;
        });
        setAiScores(fallbackScores);
      })
      .finally(() => {
        if (activeDocumentIdRef.current !== capturedDocumentId) return;
        setIsLoadingScores(false);
      });
  }, [open, fields, fieldsData, documentId, sourcefile, templateColumns]);

  const handleApprove = useCallback(async (fieldName: string) => {
    const currentlyApproved = fieldApprovals[fieldName] || false;
    const valueChanged = editedFields[fieldName] !== fields[fieldName];
    setSavingFields(prev => new Set(prev).add(fieldName));

    try {
      const scoresToSave = valueChanged
        ? { ...aiScores, [fieldName]: -1 }
        : aiScores;

      await onSave?.(editedFields, currentlyApproved ? `~${fieldName}` : fieldName, scoresToSave, aiSuggestions, aiAlternativeAnswers);
      savedFieldsRef.current = { ...savedFieldsRef.current, [fieldName]: editedFields[fieldName] };
      // Clear applied tracking for this field since it's now persisted
      setAppliedAlternativeAnswers(prev => {
        if (!prev.has(fieldName)) return prev;
        const next = new Set(prev);
        next.delete(fieldName);
        return next;
      });

      if (valueChanged) {
        setAiScores(prev => ({ ...prev, [fieldName]: -1 }));

        scoreAbortControllersRef.current.get(fieldName)?.abort();
        const controller = new AbortController();
        scoreAbortControllersRef.current.set(fieldName, controller);

        const templateColumn = templateColumns.find(col => col.name === fieldName);
        const question = templateColumn?.priming || `What is the ${fieldName.toLowerCase()}?`;
        const capturedValue = editedFields[fieldName];
        const capturedFields = editedFields;

        setRefreshingScoreFields(prev => new Set(prev).add(fieldName));
        const reviewInput={
          sourcefile,
          document_id: documentId,
          columns: [{ column_name: fieldName, column_value: capturedValue, question }],
          priming: "",
          model: model ?? "opus-46",
        };
        (reviewApiName
      ? extractionApi.reviewDataAtPath(reviewApiName, reviewInput)
      : extractionApi.reviewData(reviewInput,controller.signal)
    ).then(results => {
          if (results?.length > 0) {
            const col = results[0].columns.find((c: { column_name: string }) => c.column_name === fieldName);
            if (col) {
              setAiScores(prev => ({ ...prev, [fieldName]: col.score }));
              setAiSuggestions(prev => ({ ...prev, [fieldName]: col.suggestions || [] }));
              if (col.alternative_answer) {
                setAiAlternativeAnswers(prev => ({ ...prev, [fieldName]: col.alternative_answer! }));
              } else {
                setAiAlternativeAnswers(prev => {
                  const next = { ...prev };
                  delete next[fieldName];
                  return next;
                });
              }
              onSaveRef.current?.(
                capturedFields,
                undefined,
                { [fieldName]: col.score },
                { [fieldName]: col.suggestions || [] },
                { [fieldName]: col.alternative_answer ?? "" }
              );
            }
          }
        }).catch(err => {
          if ((err as { name?: string }).name === "AbortError") return;
        }).finally(() => {
          if (scoreAbortControllersRef.current.get(fieldName) === controller) {
            scoreAbortControllersRef.current.delete(fieldName);
            setRefreshingScoreFields(prev => {
              const next = new Set(prev);
              next.delete(fieldName);
              return next;
            });
          }
        });
      }

      setFieldApprovals(prev => ({
        ...prev,
        [fieldName]: !currentlyApproved,
      }));
    } finally {
      setSavingFields(prev => {
        const next = new Set(prev);
        next.delete(fieldName);
        return next;
      });
    }
  }, [editedFields, fields, onSave, aiScores, aiSuggestions, aiAlternativeAnswers, fieldApprovals, templateColumns, sourcefile, documentId]);

  const handleRevert = useCallback((fieldName: string) => {
    const originalStatus = fieldsData?.[fieldName]?.status;
    const wasApproved = originalStatus === "Reviewed" || originalStatus === "Approved";
    setEditedFields(prev => ({ ...prev, [fieldName]: fields[fieldName] }));
    setFieldApprovals(prev => ({ ...prev, [fieldName]: wasApproved }));
    // Clear applied tracking so the amber box can reappear after revert
    setAppliedAlternativeAnswers(prev => {
      if (!prev.has(fieldName)) return prev;
      const next = new Set(prev);
      next.delete(fieldName);
      return next;
    });
  }, [fields, fieldsData]);

  const handleFieldChange = useCallback((fieldName: string, newCleanValue: string) => {
    setEditedFields(prev => ({ ...prev, [fieldName]: newCleanValue }));
    setFieldApprovals(prev => ({ ...prev, [fieldName]: false }));
    // Clear applied tracking so the amber box can reappear if the user edits away from the alternative answer
    setAppliedAlternativeAnswers(prev => {
      if (!prev.has(fieldName)) return prev;
      const next = new Set(prev);
      next.delete(fieldName);
      return next;
    });
  }, []);

  const handleApplyAlternativeAnswer = useCallback((fieldName: string) => {
    const alternativeAnswer = aiAlternativeAnswers[fieldName];
    if (!alternativeAnswer) return;
    // Prevent re-application if already applied
    if (appliedAlternativeAnswers.has(fieldName)) return;

    // Apply the alternative answer directly, preserving its citation formatting
    setEditedFields(prev => ({ ...prev, [fieldName]: alternativeAnswer }));
    setFieldApprovals(prev => ({ ...prev, [fieldName]: false }));
    setAppliedAlternativeAnswers(prev => new Set(prev).add(fieldName));
  }, [aiAlternativeAnswers, appliedAlternativeAnswers]);

  const handleStatusChange = useCallback(async (newStatus: "Pending" | "Reviewed") => {
    onStatusChange?.(newStatus);

    setSavingStatus(newStatus);
    setSavingFields(new Set(Object.keys(editedFields)));

    try {
      const flag = newStatus === "Reviewed" ? "*" : "**";
      await onSave?.(editedFields, flag, aiScores, aiSuggestions, aiAlternativeAnswers);
      savedFieldsRef.current = { ...editedFields };
      setAppliedAlternativeAnswers(new Set());

      const newApprovalState: Record<string, boolean> = {};
      Object.keys(editedFields).forEach(fieldName => {
        newApprovalState[fieldName] = newStatus === "Reviewed";
      });
      setFieldApprovals(newApprovalState);
    } finally {
      setSavingFields(new Set());
      setSavingStatus(null);
    }
  }, [onStatusChange, editedFields, onSave, aiScores, aiSuggestions, aiAlternativeAnswers]);

  // Auto-update status when approval state changes
  useEffect(() => {
    if (savingStatus !== null) return;
    if (Object.keys(editedFields).length === 0) return;

    if (status === "Reviewed" && prevStatusRef.current !== "Reviewed") {
      prevStatusRef.current = "Reviewed";
      handleStatusChange("Reviewed");
    } else if (status === "Pending" && prevStatusRef.current === "Reviewed") {
      prevStatusRef.current = "Pending";
      onStatusChange?.("Pending");
    }
  }, [status, savingStatus, handleStatusChange, onStatusChange, editedFields]);

  const handleTextareaInput = useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    target.style.height = "auto";
    target.style.height = target.scrollHeight + "px";
  }, []);

  const handleSave = useCallback(async () => {
    const changedFieldNames = Object.keys(editedFields).filter(
      key => editedFields[key] !== fields[key]
    );
    const capturedFields = editedFields;

    // Use -1 as a sentinel meaning "scoring in progress" — clears low confidence, shows loader
    const clearingScores = changedFieldNames.reduce<Record<string, number>>(
      (acc, name) => ({ ...acc, [name]: -1 }),
      { ...aiScores }
    );

    await onSave?.(editedFields, undefined, clearingScores, aiSuggestions, aiAlternativeAnswers);
    savedFieldsRef.current = { ...editedFields };
    setAppliedAlternativeAnswers(new Set());
    setAiScores(clearingScores);

    for (const fieldName of changedFieldNames) {
      scoreAbortControllersRef.current.get(fieldName)?.abort();
      const controller = new AbortController();
      scoreAbortControllersRef.current.set(fieldName, controller);

      const templateColumn = templateColumns.find(col => col.name === fieldName);
      const question = templateColumn?.priming || `What is the ${fieldName.toLowerCase()}?`;

      setRefreshingScoreFields(prev => new Set(prev).add(fieldName));

      const reviewInput = {
        sourcefile,
        document_id: documentId,
        columns: [{ column_name: fieldName, column_value: capturedFields[fieldName], question }],
        priming: "",
        model: model ?? "opus-46",
      };
      (reviewApiName
        ? extractionApi.reviewDataAtPath(reviewApiName, reviewInput)
        : extractionApi.reviewData(reviewInput, controller.signal)
      ).then(results => {
        if (results?.length > 0) {
          const col = results[0].columns.find((c: { column_name: string }) => c.column_name === fieldName);
          if (col) {
            setAiScores(prev => ({ ...prev, [fieldName]: col.score }));
            setAiSuggestions(prev => ({ ...prev, [fieldName]: col.suggestions || [] }));
            if (col.alternative_answer) {
              setAiAlternativeAnswers(prev => ({ ...prev, [fieldName]: col.alternative_answer! }));
            } else {
              setAiAlternativeAnswers(prev => {
                const next = { ...prev };
                delete next[fieldName];
                return next;
              });
            }
            // Persist real score back to parent — updates grid even if sheet is closed
            onSaveRef.current?.(
              capturedFields,
              undefined,
              { [fieldName]: col.score },
              { [fieldName]: col.suggestions || [] },
              { [fieldName]: col.alternative_answer ?? "" }
            );
          }
        }
      }).catch(err => {
        if ((err as { name?: string }).name === "AbortError") return;
      }).finally(() => {
        if (scoreAbortControllersRef.current.get(fieldName) === controller) {
          scoreAbortControllersRef.current.delete(fieldName);
          setRefreshingScoreFields(prev => {
            const next = new Set(prev);
            next.delete(fieldName);
            return next;
          });
        }
      });
    }
  }, [editedFields, fields, onSave, aiScores, aiSuggestions, aiAlternativeAnswers, templateColumns, sourcefile, documentId, reviewApiName]);

  const handleSaveAndClose = useCallback(async () => {
    await handleSave();
    closeDocument();
    onClose();
  }, [handleSave, closeDocument, onClose]);

  const triggerUnsavedCheck = useCallback((proceed: () => void) => {
    if (hasUnsavedChanges) {
      setPendingProceed(() => proceed);
      setShowUnsavedDialog(true);
    } else {
      proceed();
    }
  }, [hasUnsavedChanges]);

  useImperativeHandle(ref, () => ({
    checkUnsavedChanges(proceed: () => void) {
      triggerUnsavedCheck(proceed);
    },
  }), [triggerUnsavedCheck]);

  const cleanedFields = useMemo(() => {
    const cleaned: Record<string, string> = {};
    Object.entries(editedFields).forEach(([key, value]) => {
      cleaned[key] = removeCitationBrackets(value);
    });
    return cleaned;
  }, [editedFields]);

  const handleDialogSaveAndClose = useCallback(async () => {
    await handleSave();
    setShowUnsavedDialog(false);
    pendingProceed?.();
    setPendingProceed(null);
  }, [handleSave, pendingProceed]);

  const handleDialogProceedWithoutSaving = useCallback(() => {
    discardedRef.current = true;
    setShowUnsavedDialog(false);
    pendingProceed?.();
    setPendingProceed(null);
  }, [pendingProceed]);

  return (
    <TooltipProvider>
      <Sheet
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            const doClose = () => { closeDocument(); onClose(); };
            if (hasUnsavedChanges) {
              setPendingProceed(() => doClose);
              setShowUnsavedDialog(true);
            } else {
              doClose();
            }
          }
        }}
        modal={false}
      >
        <SheetContent className="w-full md:w-[40svw] sm:max-w-none flex flex-col p-0 gap-0 bg-gray-100" onFocusOutside={(e) => e.preventDefault()} onPointerDownOutside={(e) => { if (viewingDoc) { e.preventDefault(); } }}>
          <SheetHeader className="px-4 pt-6 pb-4 bg-white border-b shrink-0">
            <SheetTitle className="text-lg font-semibold">Data Validation</SheetTitle>
          </SheetHeader>

          <div className="p-4 bg-gray-100 border-b shrink-0">
            <p className="text-sm font-normal text-foreground leading-relaxed">{studyName}</p>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-6">
            <div className="space-y-6">
              {Object.keys(editedFields).map((fieldName) => {
                const isApproved = fieldApprovals[fieldName] || false;
                const isSaving = savingFields.has(fieldName);
                const isRefreshingScore = refreshingScoreFields.has(fieldName);
                const aiScore = aiScores[fieldName];
                const suggestions = aiSuggestions[fieldName] || [];
                const alternativeAnswer = aiAlternativeAnswers[fieldName];
                const fieldText = editedFields[fieldName] || "";
                const { citations } = parseCellCitations(fieldText);
                const showAlternativeAnswer = !isLoadingScores && !isRefreshingScore &&
                  aiScore !== undefined && aiScore !== -1 &&
                  aiScore < AI_SCORE_LOW_CONFIDENCE_THRESHOLD && !!alternativeAnswer &&
                  !appliedAlternativeAnswers.has(fieldName);

                return (
                  <div key={fieldName} className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">{fieldName}</span>
                        {citations.length > 0 && (
                          <>
                            {citations.map((citation) => {
                              const doc = documents.find(d => d.filename === citation.filename);
                              return (
                                <button
                                  key={`${citation.filename}-${citation.number}`}
                                  type="button"
                                  onClick={() => {
                                    if (doc) {
                                      openDocument({ doc, page: citation.page ?? "", sourceText: citation.sourceText, side: "left", showTabs: true, hideOverlay: true });
                                    }
                                  }}
                                  disabled={!doc}
                                  className="text-xs font-semibold text-gray-700 bg-gray-200 px-2 py-0.5 rounded hover:bg-gray-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={citation.sourceText || `${citation.filename} ${citation.page}`}
                                >
                                  {citation.number}
                                </button>
                              );
                            })}
                          </>
                        )}
                        {!isLoadingScores && !isRefreshingScore && (
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <HelpCircle className="w-4 h-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="right"
                              className="max-w-md p-4 bg-white border border-gray-200 shadow-lg z-[60]"
                              sideOffset={8}
                            >
                              <div className="space-y-2">
                                <p className="font-semibold text-sm text-foreground">AI Suggestions:</p>
                                {suggestions.length > 0 ? (
                                  <ul className="list-disc pl-4 space-y-1.5 text-xs text-gray-700">
                                    {suggestions.map((suggestion, idx) => (
                                      <li key={`${fieldName}-suggestion-${idx}`} className="leading-relaxed pl-1">{suggestion}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-xs text-gray-600">No suggestions available</p>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      {(isLoadingScores || isRefreshingScore || aiScore === -1 || aiScore === undefined || (aiScore !== undefined && aiScore < AI_SCORE_LOW_CONFIDENCE_THRESHOLD && !appliedAlternativeAnswers.has(fieldName))) && (
                        isLoadingScores || isRefreshingScore || aiScore === -1 ? (
                          <span className="inline-flex gap-1 items-center text-sm text-gray-600">
                            AI Review: <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          </span>
                        ) : (
                          <div className="flex items-center gap-2 text-sm text-amber-500">
                            <AlertCircle className="w-4 h-4" /> Low Confidence
                          </div>
                        )
                      )}
                    </div>

                    <div className="relative">
                      <textarea
                        value={cleanedFields[fieldName] || ""}
                        onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                        onInput={handleTextareaInput}
                        className="w-full min-h-[100px] max-h-[400px] text-sm resize-none pr-16 px-3 py-2.5 border border-gray-300 leading-relaxed overflow-y-auto focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter value..."
                        style={{ height: "auto" }}
                      />

                      <div data-field-actions className="absolute top-2.5 right-5 flex flex-col gap-2.5">
                        <button
                          type="button"
                          onClick={() => handleApprove(fieldName)}
                          disabled={isSaving}
                          className={`w-9 h-9 flex items-center justify-center border transition-all shadow-sm ${getButtonClassName(isSaving, isApproved)}`}
                          title={isSaving ? "Saving..." : isApproved ? "Approved" : "Approve"}
                        >
                          {isSaving ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRevert(fieldName)}
                          disabled={isSaving || editedFields[fieldName] === fields[fieldName]}
                          className={`w-9 h-9 flex items-center justify-center border transition-all shadow-sm ${
                            isSaving || editedFields[fieldName] === fields[fieldName]
                              ? "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed"
                              : "border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50"
                          }`}
                          title="Revert to original"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {showAlternativeAnswer && (
                      <div className="border border-amber-200 bg-amber-50 rounded px-3 py-2.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-amber-700 mb-1">AI Alternative Answer:</p>
                            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap break-words">{removeCitationBrackets(alternativeAnswer)}</p>
                            {citations.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {citations.map((citation) => {
                                  const doc = documents.find(d => d.filename === citation.filename);
                                  return (
                                    <button
                                      key={`alt-${citation.filename}-${citation.number}`}
                                      type="button"
                                      disabled={!doc}
                                      onClick={() => {
                                        if (doc) openDocument({ doc, page: citation.page ?? "", sourceText: citation.sourceText, side: "left", showTabs: true, hideOverlay: true });
                                      }}
                                      className="text-xs font-semibold text-amber-800 bg-amber-200 px-2 py-0.5 rounded hover:bg-amber-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                      title={citation.sourceText || `${citation.filename} ${citation.page}`}
                                    >
                                      {citation.number}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100 hover:text-amber-800"
                            onClick={() => handleApplyAlternativeAnswer(fieldName)}
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white border-t shrink-0 p-4">
            <div className="flex gap-3">
              <Button
                variant="success"
                size="lg"
                className="flex-1"
                onClick={async () => { await handleSave(); await handleStatusChange("Reviewed"); }}
                disabled={savingStatus !== null}
              >
                {savingStatus === "Reviewed" && <Loader2 className="w-4 h-4 animate-spin" />}
                Reviewed
              </Button>
              <Button variant="outline" size="lg" className="flex-1" onClick={handleSave}>
                Save
              </Button>
              <Button variant="outline" size="lg" className="flex-1" onClick={handleSaveAndClose}>
                Save and Close
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Do you want to save before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDialogProceedWithoutSaving}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDialogSaveAndClose}>
              Save and close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
});

DataValidationSheet.displayName = "DataValidationSheet";
